import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { createEnrollment, getEnrollmentConstants, createEnrollmentConstant } from '../../_components/free-users/apiService';
import {
  PersonOutline,
  CreditCard,
  Description,
  Assignment,
  Add,
  Delete,
  ArrowBack,
  InfoOutlined,
} from '@material-ui/icons';
import {
  Select,
  MenuItem,
  FormControl,
  Dialog,
  CircularProgress,
  Radio,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';

// ─── Brand tokens ────────────────────────────────────────────────────────────
const PRIMARY = '#4F46E5';
const TEXT_PRIMARY = '#111827';
const TEXT_LABEL = '#374151';
const TEXT_SECONDARY = '#6B7280';
const BORDER = '#E5E7EB';
const REQUIRED = '#f00037';
const FONT = "'Gellix', 'Inter', sans-serif";
const SECTION_DIVIDER = '#F3F4F6';

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inputStyle = (disabled = false) => ({
  width: '100%',
  height: 36,
  backgroundColor: disabled ? '#F9FAFB' : '#fff',
  border: `1px solid ${disabled ? BORDER : BORDER}`,
  borderRadius: 4,
  padding: '0 10px',
  fontSize: 13,
  color: disabled ? '#9CA3AF' : TEXT_PRIMARY,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: FONT,
});

const labelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: TEXT_LABEL,
  marginBottom: 6,
  display: 'block',
  fontFamily: FONT,
};

const primaryBtn = {
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  backgroundColor: PRIMARY,
  border: 'none',
  borderRadius: 4,
  padding: '8px 20px',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
  fontFamily: FONT,
};

const ghostBtn = {
  fontSize: 13,
  fontWeight: 600,
  color: TEXT_LABEL,
  backgroundColor: '#fff',
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  padding: '8px 20px',
  cursor: 'pointer',
  fontFamily: FONT,
};

// ─── MUI Select brand override ────────────────────────────────────────────────
const useSelectStyles = makeStyles(() => ({
  root: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      backgroundColor: '#fff',
      '& fieldset': { borderColor: BORDER },
      '&:hover fieldset': { borderColor: '#9CA3AF' },
      '&.Mui-focused fieldset': { borderColor: PRIMARY },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 10px',
      height: 36,
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
      color: TEXT_PRIMARY,
      fontFamily: FONT,
    },
    '& .MuiSelect-outlined': {
      padding: '0 10px',
      height: 36,
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

// ─── Initial state ────────────────────────────────────────────────────────────
const INITIAL_LOAN = {
  loanSource: '', loanAmount: '', loanTenure: '',
  financingPhone: '', financingEmail: '', loanAppId: '', financeUtr: '',
};
const INITIAL_FORM = {
  saleOwner: '', saleManager: '', program: '', course: '', studentPhone: '',
  seatBookingAmount: '', downPaymentAmount: '', downPaymentSource: '',
  loanEntries: [{ ...INITIAL_LOAN }],
  placementOption: '', isCustomPlacement: false,
  paidAmount: '', placementAmount: '', paymentReference: '', otherRemarks: '',
};

const PLACEMENT_OPTIONS = [
  { value: 'already_paid', label: 'Already Paid' },
  { value: 'pay_after_placement', label: 'Pay After Placement' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <div style={{
        background: '#EEF2FF', borderRadius: '50%', padding: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon style={{ fontSize: 16, color: PRIMARY }} />
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
        {title}
      </span>
    </div>
  );
}

function FieldLabel({ label, required }) {
  return (
    <span style={labelStyle}>
      {label}
      {required && <span style={{ color: REQUIRED }}> *</span>}
    </span>
  );
}

function SectionDivider() {
  return <div style={{ borderTop: `1px solid ${SECTION_DIVIDER}`, margin: '24px 0' }} />;
}

// Dropdown that can add new source options inline
function AddableSelect({ value, onChange, sources, onAddSource }) {
  const cls = useSelectStyles();
  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState('');

  const handleAdd = () => {
    if (!inputVal.trim()) return;
    onAddSource(inputVal.trim());
    onChange(inputVal.trim());
    setInputVal('');
    setShowInput(false);
  };

  return (
    <div>
      <FormControl variant="outlined" fullWidth className={cls.root}>
        <Select
          value={value}
          onChange={e => {
            if (e.target.value === '__add_new__') { setShowInput(true); }
            else { onChange(e.target.value); }
          }}
          displayEmpty
          renderValue={value ? undefined : () => (
            <span style={{ color: '#9CA3AF', fontSize: 13, fontFamily: FONT }}>Select source</span>
          )}
        >
          {sources.map(s => (
            <MenuItem key={s} value={s} style={{ fontSize: 13, fontFamily: FONT }}>{s}</MenuItem>
          ))}
          <MenuItem value="__add_new__" style={{ fontSize: 13, fontFamily: FONT, color: PRIMARY }}>
            <Add fontSize="small" style={{ marginRight: 4, fontSize: 15 }} />
            Add New Source
          </MenuItem>
        </Select>
      </FormControl>

      {showInput && (
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <input
            style={{ ...inputStyle(), flex: 1 }}
            placeholder="New source name"
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            autoFocus
          />
          <button style={primaryBtn} onClick={handleAdd}>Add</button>
          <button style={ghostBtn} onClick={() => setShowInput(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// Native select wrapper (brand-styled)
function BrandSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        ...inputStyle(),
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%236B7280'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        paddingRight: 30,
        cursor: 'pointer',
      }}
    >
      <option value="" disabled>{placeholder || 'Select'}</option>
      {options.map(o => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function EnrollPayment() {
  const location = useLocation();
  const history = useHistory();
  const userParams = location.state?.userParams || {};

  const [form, setForm] = useState({ ...INITIAL_FORM, studentPhone: userParams.phone || '' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downPaymentSources, setDownPaymentSources] = useState([]);
  const [financeSources, setFinanceSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);

  // Fetch enrollment constants from backend on mount
  useEffect(() => {
    getEnrollmentConstants()
      .then((res) => {
        // Response shape: { success: true, data: { PRODUCT: [...], COURSE: [...],
        //   DOWN_PAYMENT_SOURCE: [...], FINANCE_SOURCE: [...] } }
        const constants = res?.data ?? res ?? {};
        if (Array.isArray(constants?.PRODUCT) && constants.PRODUCT.length) {
          setProducts(constants.PRODUCT);
        }
        if (Array.isArray(constants?.COURSE) && constants.COURSE.length) {
          setCourses(constants.COURSE);
        }
        if (Array.isArray(constants?.DOWN_PAYMENT_SOURCE) && constants.DOWN_PAYMENT_SOURCE.length) {
          setDownPaymentSources(constants.DOWN_PAYMENT_SOURCE);
        }
        if (Array.isArray(constants?.FINANCE_SOURCE) && constants.FINANCE_SOURCE.length) {
          setFinanceSources(constants.FINANCE_SOURCE);
        }
      })
      .catch(() => {});
  }, []);

  const addDownPaymentSource = async (name) => {
    setDownPaymentSources(prev => prev.includes(name) ? prev : [...prev, name]);
    try {
      await createEnrollmentConstant({ name, type: 'DOWN_PAYMENT_SOURCE' });
    } catch (err) {
      console.error('Failed to save down payment source constant', err);
    }
  };

  const addFinanceSource = async (name) => {
    setFinanceSources(prev => prev.includes(name) ? prev : [...prev, name]);
    try {
      await createEnrollmentConstant({ name, type: 'FINANCE_SOURCE' });
    } catch (err) {
      console.error('Failed to save finance source constant', err);
    }
  };

  const saleAmount = useMemo(() => {
    const seat = parseFloat(form.seatBookingAmount) || 0;
    const down = parseFloat(form.downPaymentAmount) || 0;
    const loans = form.loanEntries.reduce((s, e) => s + (parseFloat(e.loanAmount) || 0), 0);
    return seat + down + loans;
  }, [form.seatBookingAmount, form.downPaymentAmount, form.loanEntries]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const setLoan = (i, field, value) =>
    setForm(prev => {
      const entries = [...prev.loanEntries];
      entries[i] = { ...entries[i], [field]: value };
      return { ...prev, loanEntries: entries };
    });

  const addLoan = () =>
    setForm(prev => ({ ...prev, loanEntries: [...prev.loanEntries, { ...INITIAL_LOAN }] }));

  const removeLoan = (i) =>
    setForm(prev => ({ ...prev, loanEntries: prev.loanEntries.filter((_, idx) => idx !== i) }));

  const handleSave = () => {
    if (!form.saleOwner || !form.program || !form.course || !form.placementOption) {
      toast.error('Please fill in all required fields');
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const firstLoan = form.loanEntries[0] || {};
      const payload = {
        date: new Date().toISOString().split('T')[0],
        studentName: [userParams.firstName, userParams.lastName].filter(Boolean).join(' '),
        studentEmail: userParams.email || '',
        studentPhone: form.studentPhone.trim(),
        saleOwner: form.saleOwner.trim(),
        saleManager: form.saleManager.trim(),
        product: form.program,
        course: form.course,
        saleAmount,
        placementAmount: Number(form.placementAmount) || 0,
        masterSeatBookingAmount: Number(form.seatBookingAmount) || 0,
        downPaymentAmount: Number(form.downPaymentAmount) || 0,
        loanAmount: Number(firstLoan.loanAmount) || 0,
        loanTenureMonths: Number(firstLoan.loanTenure) || undefined,
        downPaymentSource: form.downPaymentSource || undefined,
        loanFinancingSource: firstLoan.loanSource || undefined,
        financingPersonPhone: firstLoan.financingPhone || undefined,
        financingPersonEmail: firstLoan.financingEmail || undefined,
        loanApplicationId: firstLoan.loanAppId || undefined,
        financeUTR: firstLoan.financeUtr || undefined,
        notes: form.otherRemarks.trim() || undefined,
      };
      await createEnrollment(payload);
      toast.success('Enrollment created successfully');
      setShowConfirm(false);
      history.goBack();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create enrollment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: FONT }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => history.goBack()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            borderRadius: 4, display: 'flex', alignItems: 'center', color: TEXT_SECONDARY }}
        >
          <ArrowBack style={{ fontSize: 20 }} />
        </button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, margin: 0, fontFamily: FONT }}>
            Add New Sale Record
          </h1>
          {userParams.firstName && (
            <p style={{ fontSize: 13, color: TEXT_SECONDARY, margin: '4px 0 0', fontFamily: FONT }}>
              {userParams.firstName} {userParams.lastName}
              {userParams.email && <span style={{ marginLeft: 8 }}>· {userParams.email}</span>}
            </p>
          )}
        </div>
      </div>

      {/* ── Form card ── */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 16,
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}>
        <div style={{ padding: 24 }}>

          {/* ── Section 1: Ownership & Program ── */}
          <SectionHeader icon={PersonOutline} title="Ownership & Program" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div>
              <FieldLabel label="Sale Owner" required />
              <input
                style={inputStyle()}
                value={form.saleOwner}
                onChange={e => set('saleOwner', e.target.value)}
                placeholder="Sale owner name"
              />
            </div>
            <div>
              <FieldLabel label="Sale Manager" required />
              <input
                style={inputStyle()}
                value={form.saleManager}
                onChange={e => set('saleManager', e.target.value)}
                placeholder="Sale manager name"
              />
            </div>
            <div>
              <FieldLabel label="Product" required />
              <BrandSelect
                value={form.program}
                onChange={v => set('program', v)}
                options={products}
                placeholder="Select product"
              />
            </div>
            <div>
              <FieldLabel label="Course" required />
              <BrandSelect
                value={form.course}
                onChange={v => set('course', v)}
                options={courses}
                placeholder="Select course"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 16 }}>
            <div>
              <FieldLabel label="Student Phone" />
              <input
                style={inputStyle()}
                value={form.studentPhone}
                onChange={e => set('studentPhone', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
              />
            </div>
          </div>

          <SectionDivider />

          {/* ── Section 2: Payment ── */}
          <SectionHeader icon={CreditCard} title="Payment" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <div>
              <FieldLabel label="Seat Booking Amount (Optional)" />
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                <input
                  style={{ ...inputStyle(), paddingLeft: 24 }}
                  type="number"
                  value={form.seatBookingAmount}
                  onChange={e => set('seatBookingAmount', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <FieldLabel label="Down Payment Amount (Optional)" />
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                <input
                  style={{ ...inputStyle(), paddingLeft: 24 }}
                  type="number"
                  value={form.downPaymentAmount}
                  onChange={e => set('downPaymentAmount', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <FieldLabel label="Down Payment Source (Optional)" />
              <AddableSelect
                value={form.downPaymentSource}
                onChange={v => set('downPaymentSource', v)}
                sources={downPaymentSources}
                onAddSource={addDownPaymentSource}
              />
            </div>
          </div>

          <SectionDivider />

          {/* ── Section 3: Loan & Financing ── */}
          <SectionHeader icon={Description} title="Loan & Financing Details" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {form.loanEntries.map((entry, i) => (
              <div key={i} style={{
                background: '#F9FAFB',
                borderRadius: 12,
                padding: '24px 16px 16px',
                position: 'relative',
                border: `1px solid ${SECTION_DIVIDER}`,
              }}>
                {form.loanEntries.length > 1 && (
                  <button
                    onClick={() => removeLoan(i)}
                    style={{ position: 'absolute', top: 8, right: 10, background: 'none',
                      border: 'none', cursor: 'pointer', padding: 2 }}
                  >
                    <Delete style={{ fontSize: 16, color: '#EF4444' }} />
                  </button>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
                  <div>
                    <FieldLabel label="Finance Source" required />
                    <AddableSelect
                      value={entry.loanSource}
                      onChange={v => setLoan(i, 'loanSource', v)}
                      sources={financeSources}
                      onAddSource={addFinanceSource}
                    />
                  </div>
                  <div>
                    <FieldLabel label="Loan Amount" required />
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                      <input
                        style={{ ...inputStyle(), paddingLeft: 24 }}
                        type="number"
                        value={entry.loanAmount}
                        onChange={e => setLoan(i, 'loanAmount', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <FieldLabel label="Loan Tenure" required />
                    <input
                      style={inputStyle()}
                      value={entry.loanTenure}
                      onChange={e => setLoan(i, 'loanTenure', e.target.value)}
                      placeholder="e.g. 24 months"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <FieldLabel label="Financing Person's Phone" required />
                    <input
                      style={inputStyle()}
                      value={entry.financingPhone}
                      onChange={e => setLoan(i, 'financingPhone', e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <FieldLabel label="Financing Person's Email" required />
                    <input
                      style={inputStyle()}
                      value={entry.financingEmail}
                      onChange={e => setLoan(i, 'financingEmail', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <FieldLabel label="Loan Application ID" required />
                    <input
                      style={inputStyle()}
                      value={entry.loanAppId}
                      onChange={e => setLoan(i, 'loanAppId', e.target.value)}
                      placeholder="Application ID"
                    />
                  </div>
                  <div>
                    <FieldLabel label="Finance UTR" required />
                    <input
                      style={inputStyle()}
                      value={entry.financeUtr}
                      onChange={e => setLoan(i, 'financeUtr', e.target.value)}
                      placeholder="UTR number"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={addLoan}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  border: `1px dashed ${BORDER}`, borderRadius: 8,
                  padding: '10px 32px', background: 'transparent', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: FONT,
                }}
              >
                <Add style={{ fontSize: 16 }} />
                Add Another Financing Details
              </button>
            </div>
          </div>

          <SectionDivider />

          {/* ── Section 4: Summary & Other Information ── */}
          <SectionHeader icon={Assignment} title="Summary & Other Information" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <FieldLabel label="Sale Amount" />
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                <input
                  style={{ ...inputStyle(true), paddingLeft: 24 }}
                  value={saleAmount.toLocaleString('en-IN')}
                  readOnly
                />
              </div>
              <p style={{ fontSize: 12, color: TEXT_SECONDARY, margin: '4px 0 0', fontFamily: FONT }}>
                Sum of seat booking, down payment, and total loan amounts.
              </p>
            </div>
            <div>
              <FieldLabel label="Placement Option" required />
              <BrandSelect
                value={form.placementOption}
                onChange={v => { set('placementOption', v); set('isCustomPlacement', false); }}
                options={PLACEMENT_OPTIONS}
                placeholder="Select payment option"
              />
            </div>
          </div>

          {/* Conditional: Already Paid */}
          {form.placementOption === 'already_paid' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <FieldLabel label="Paid Amount" required />
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                  <input
                    style={{ ...inputStyle(), paddingLeft: 24 }}
                    type="number"
                    value={form.paidAmount}
                    onChange={e => set('paidAmount', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <FieldLabel label="Payment Reference" />
                <input
                  style={inputStyle()}
                  value={form.paymentReference}
                  onChange={e => set('paymentReference', e.target.value)}
                  placeholder="Reference number"
                />
              </div>
            </div>
          )}

          {/* Conditional: Pay After Placement */}
          {form.placementOption === 'pay_after_placement' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 12 }}>
                <Radio
                  checked={form.isCustomPlacement}
                  onChange={e => set('isCustomPlacement', e.target.checked)}
                  style={{ color: PRIMARY, padding: 0 }}
                  size="small"
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: FONT }}>
                  Custom
                </span>
              </label>
              {form.isCustomPlacement && (
                <div style={{ maxWidth: 'calc(50% - 8px)' }}>
                  <FieldLabel label="Placement Amount" required />
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 13, color: TEXT_SECONDARY, pointerEvents: 'none' }}>₹</span>
                    <input
                      style={{ ...inputStyle(), paddingLeft: 24 }}
                      type="number"
                      value={form.placementAmount}
                      onChange={e => set('placementAmount', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Other Remarks */}
          <div>
            <FieldLabel label="Other Remarks" />
            <textarea
              style={{
                ...inputStyle(),
                height: 80,
                padding: '8px 10px',
                resize: 'vertical',
                lineHeight: 1.5,
              }}
              value={form.otherRemarks}
              onChange={e => set('otherRemarks', e.target.value)}
              placeholder="Add any remarks..."
            />
          </div>

        </div>

        {/* ── Footer ── */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12,
          padding: '16px 24px', borderTop: `1px solid ${SECTION_DIVIDER}`,
        }}>
          <button style={ghostBtn} onClick={() => setForm({ ...INITIAL_FORM })}>
            Reset
          </button>
          <button style={primaryBtn} onClick={handleSave}>
            Save Sale Record
          </button>
        </div>
      </div>

      {/* ── Confirmation dialog ── */}
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
      >
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          {/* Concentric circles */}
          <div style={{
            width: 48, height: 48, borderRadius: '50%', background: '#EEF2FF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: '#C7D2FE',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <InfoOutlined style={{ fontSize: 18, color: PRIMARY }} />
            </div>
          </div>

          <p style={{
            fontSize: 18, fontWeight: 700, color: PRIMARY,
            textAlign: 'center', margin: 0, fontFamily: FONT,
          }}>
            Confirm New Sale Record?
          </p>

          <div style={{ display: 'flex', gap: 12, width: '100%' }}>
            <button
              onClick={() => setShowConfirm(false)}
              style={{ ...ghostBtn, flex: 1, textAlign: 'center' }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              style={{ ...primaryBtn, flex: 1, textAlign: 'center', opacity: isSubmitting ? 0.7 : 1 }}
            >
              {isSubmitting
                ? <CircularProgress size={14} style={{ color: '#fff' }} />
                : 'Add Booking'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
